import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { AlertTriangle, ChevronRight } from 'lucide-preact'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import type { ValidationError } from '@/utils/errorHandling'
import { toReadinessItems } from '@/constants/signOffReadiness'

interface Props {
    /** Server-computed outstanding list from the Web view DTO. */
    outstanding: ValidationError[] | undefined
    routeToTab: (tab: WebTabKey) => void
    /** The tab already on screen, so a row does not offer to take the reader where they are. */
    activeTab: WebTabKey
}

/**
 * Count of what is still outstanding before sign-off, sitting immediately before the sign-off
 * control so the gate and the reason it will fail are adjacent (ORB-460).
 *
 * The chip is the trigger, not the list — a five-item checklist would blow out a header already
 * carrying a title, a date, three chips, audiences and the progress card. It renders nothing when
 * the count is zero: at that point the Sign Off button is enabled and needs no explanation.
 *
 * Amber treatment matches SpeakerCard's missing-fields chip, the app's existing completeness
 * affordance.
 */
/** Gap between the chip and the panel, and the breathing room kept below the panel. */
const PANEL_OFFSET = 8

export function SignOffReadinessChip({ outstanding, routeToTab, activeTab }: Props) {
    const [open, setOpen] = useState(false)
    /** Where to pin the panel, measured from the chip at open time. Null until measured. */
    const [anchor, setAnchor] = useState<{ top: number; left: number; maxHeight: number } | null>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    /** The portalled panel, so a scroll inside it can be told apart from a page scroll. */
    const panelRef = useRef<HTMLDivElement>(null)
    const items = toReadinessItems(outstanding)

    const close = useCallback(() => setOpen(false), [])

    /**
     * ⚑⚑ Measured and pinned to the viewport, because the panel is portalled out of the page
     * (ORB-889). It used to be `absolute` inside the header, and the page content wrapper —
     * `max-w-[85rem] mx-auto overflow-hidden` — clipped it: the chip said "6 before sign-off" and
     * the sixth row was sliced through, its "Details ›" gone with it. The one item a reader could
     * not see was the one they could not click.
     *
     * The wrapper clips on purpose, and anything else that pops out of it has the same bug waiting,
     * so the panel leaves the container rather than the container being opened up.
     *
     * `maxHeight` is the second half of the fix and the durable one. Six items fit; twenty would
     * not, and a panel that runs off the bottom of the screen is the same defect with a different
     * cause. This one scrolls instead.
     */
    const openPanel = useCallback(() => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect) {
            const top = rect.bottom + PANEL_OFFSET
            setAnchor({ top, left: rect.left, maxHeight: window.innerHeight - top - PANEL_OFFSET })
        }
        setOpen(true)
    }, [])

    useEffect(() => {
        if (!open) return
        const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close() }

        /**
         * ⚑⚑ Closes when the PAGE scrolls, never when the panel's own list does.
         *
         * The panel is pinned to the viewport, so it no longer travels with the chip and has to close
         * rather than drift away from it. That needs the capture phase, because the scroll happens on
         * an inner container and does not bubble to window.
         *
         * Which is exactly the trap: capture also delivers the scroll from the panel's own
         * `overflow-y-auto` list. Closing on that makes the max-height unusable — the list exists to
         * be scrolled, and scrolling it would dismiss the thing being read. That would have undone
         * the fix this component just received. Raised by Copilot on PR #144.
         */
        const onScroll = (event: Event) => {
            if (panelRef.current?.contains(event.target as Node)) return
            close()
        }

        window.addEventListener('keydown', onKey)
        window.addEventListener('scroll', onScroll, true)
        window.addEventListener('resize', close)
        return () => {
            window.removeEventListener('keydown', onKey)
            window.removeEventListener('scroll', onScroll, true)
            window.removeEventListener('resize', close)
        }
    }, [open, close])

    if (items.length === 0) return null

    return (
        <span class="relative inline-flex">
            <button
                ref={triggerRef}
                type="button"
                onClick={() => (open ? close() : openPanel())}
                aria-expanded={open}
                aria-label={`${items.length} ${items.length === 1 ? 'item' : 'items'} outstanding before sign-off`}
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
                <AlertTriangle class="w-3.5 h-3.5 flex-shrink-0" />
                {items.length} before sign-off
            </button>

            {open && anchor && createPortal(
                <>
                    {/* Closes on an outside click. A backdrop rather than a document listener,
                        because the panel is no longer a descendant of this component's container —
                        a `contains(event.target)` check would treat every click inside the panel as
                        an outside one and close it before a "Details ›" could fire. */}
                    <div class="fixed inset-0 z-[60]" onClick={close} />
                    <div
                        ref={panelRef}
                        class="fixed z-[61] w-72 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg overflow-hidden flex flex-col"
                        style={{ top: `${anchor.top}px`, left: `${anchor.left}px`, maxHeight: `${anchor.maxHeight}px` }}
                    >
                    {/* ⚑ Reads as a title rather than as the first item (ORB-889). It was one weight
                        step from the rows below it, in the same colour, with only a divider between —
                        so a reader scanning the list started at the heading. Smaller, uppercase and
                        tracked, on a tinted bar: the treatment the app already uses for a section label
                        in the web header ("Broadcast Date", "Audiences"), not a new one invented here. */}
                    <div class="flex-shrink-0 px-3.5 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 text-[11px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">
                        Needed before sign-off
                    </div>
                    {/* The list scrolls, not the panel: the title stays put so a reader who has
                        scrolled still knows what they are looking at. */}
                    <ul class="overflow-y-auto">
                        {items.map((item, index) => (
                            <li
                                key={`${item.message}-${index}`}
                                class="border-b border-slate-200 dark:border-slate-700 last:border-b-0"
                            >
                                {item.tab && item.tab !== activeTab ? (
                                    <button
                                        type="button"
                                        onClick={() => { setOpen(false); routeToTab(item.tab as WebTabKey) }}
                                        class="w-full flex items-center justify-between gap-3 px-3.5 py-2 text-left text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        <span>{item.message}</span>
                                        <span class="inline-flex items-center gap-0.5 text-[11px] text-slate-400 dark:text-slate-500 flex-shrink-0">
                                            {item.tabLabel}
                                            <ChevronRight class="w-3 h-3" />
                                        </span>
                                    </button>
                                ) : (
                                    // Plain text, for two reasons that land here together.
                                    //
                                    // No tab owns this field name, so there is nothing to link to — showing
                                    // the server's message alone beats guessing a destination.
                                    //
                                    // ⚑ Or the owning tab is the one already on screen (ORB-889). Every
                                    // field root in FIELD_ROOT_TO_TAB maps to `details`, and this chip lives
                                    // in the header above the Details tab — so the "Details ›" affordance
                                    // was, in the common case, offering to route a reader to where they
                                    // already were. The popover closed and nothing moved, which reads as a
                                    // broken link rather than as a no-op. The jump still works when the chip
                                    // is read from another tab, which is the case it was built for.
                                    <div class="px-3.5 py-2 text-xs text-slate-700 dark:text-slate-200">
                                        {item.message}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                    </div>
                </>,
                document.body
            )}
        </span>
    )
}
